import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { HelpCircle, Plus, Trash2, Save, Lock } from "lucide-react";

const TYPE_LABELS = {
    scale_1_5: "Escala 1-5",
    single_choice: "Escolha única",
    multi_choice: "Escolha múltipla",
    text: "Texto livre",
    number: "Número",
};

const TYPE_OPTIONS = [
    { value: "scale_1_5", label: "Escala 1-5" },
    { value: "single_choice", label: "Escolha única" },
    { value: "multi_choice", label: "Escolha múltipla" },
    { value: "text", label: "Texto livre" },
    { value: "number", label: "Número" },
];

const GROUP_LABELS = {
    fib: "FIB (Felicidade Interna Bruta)",
    whr: "World Happiness Report",
    global: "Geral",
    custom: "Personalizadas",
};

const DEFAULT_DRAFT = {
    text: "",
    type: "scale_1_5",
    scope: "individual",
    mandatory: true,
    options: "",
    group: "custom",
    order: 100,
    indices: [],
};

export default function CompanyQuestionsPage() {
    const { user } = useAuth();
    const [phase, setPhase] = useState(1);
    const [questions, setQuestions] = useState([]);
    const [indices, setIndices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [draft, setDraft] = useState(DEFAULT_DRAFT);
    const [saving, setSaving] = useState(false);
    const canManage = ["company_manager", "happiness_manager", "super_admin"].includes(user?.role || "");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [q, idx] = await Promise.all([
                api.get(`/happiness/phases/${phase}/questions`),
                api.get("/happiness/indices"),
            ]);
            setQuestions(q.data || []);
            setIndices(idx.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [phase]);

    useEffect(() => { load(); }, [load]);

    const grouped = useMemo(() => {
        const g = { fib: [], whr: [], global: [], custom: [] };
        for (const q of questions) {
            (g[q.group] || g.custom).push(q);
        }
        Object.values(g).forEach((arr) => arr.sort((a, b) => a.order - b.order));
        return g;
    }, [questions]);

    const submit = async (e) => {
        e.preventDefault();
        if (!draft.text.trim()) { toast.error("Texto da pergunta obrigatório"); return; }
        setSaving(true);
        try {
            const options = draft.options.trim()
                ? draft.options.split("\n").map((s) => s.trim()).filter(Boolean)
                : [];
            const needsOptions = ["single_choice", "multi_choice"].includes(draft.type);
            if (needsOptions && options.length < 2) {
                toast.error("Perguntas de escolha precisam de pelo menos 2 opções (uma por linha)");
                setSaving(false);
                return;
            }
            await api.post("/company/happiness/questions", {
                text: draft.text.trim(),
                type: draft.type,
                phase,
                scope: draft.scope,
                mandatory: draft.mandatory,
                options,
                group: "custom",
                order: Number(draft.order) || 100,
                indices: draft.indices,
            });
            toast.success("Pergunta adicionada");
            setDraft(DEFAULT_DRAFT);
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (q) => {
        try {
            await api.patch(`/happiness/questions/${q.id}`, { active: !q.active });
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (q) => {
        if (!window.confirm(`Eliminar a pergunta "${q.text.slice(0, 60)}..."?`)) return;
        try {
            await api.delete(`/happiness/questions/${q.id}`);
            toast.success("Pergunta eliminada");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const toggleIndex = (idxId) => {
        setDraft((d) => ({
            ...d,
            indices: d.indices.includes(idxId)
                ? d.indices.filter((x) => x !== idxId)
                : [...d.indices, idxId],
        }));
    };

    return (
        <div className="space-y-10" data-testid="company-questions-page">
            <div>
                <div className="label-caps flex items-center gap-2"><HelpCircle size={14} /> Inquéritos</div>
                <h1 className="font-display text-5xl text-[#0d1628] mt-4">Perguntas do inquérito</h1>
                <p className="text-[#52525b] mt-4 max-w-2xl">
                    Consulte as perguntas globais (IHTP) e adicione as suas próprias perguntas personalizadas por fase.
                    As respostas alimentam as estatísticas e comparações do ecossistema.
                </p>
            </div>

            {/* Phase tabs */}
            <div className="flex gap-2 flex-wrap" data-testid="phase-tabs">
                {[1, 2, 3, 4, 5].map((p) => (
                    <button
                        key={p}
                        onClick={() => setPhase(p)}
                        className={`px-4 py-2 text-sm border transition-colors ${
                            phase === p
                                ? "bg-[#0d1628] text-white border-[#0d1628]"
                                : "bg-white text-[#0d1628] border-[#e4e4e7] hover:border-[#0d1628]"
                        }`}
                        data-testid={`phase-tab-${p}`}
                    >
                        Fase {p}
                    </button>
                ))}
            </div>

            {canManage && (
                <section className="card-flat" data-testid="new-question-card">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="label-caps">Adicionar pergunta personalizada</div>
                            <p className="text-sm text-[#52525b] mt-1.5">
                                Só visível para a sua empresa. Fase atual: <strong>Fase {phase}</strong>.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn-outline"
                            onClick={() => setShowForm(!showForm)}
                            data-testid="toggle-new-question"
                        >
                            <Plus size={14} /> {showForm ? "Fechar" : "Nova pergunta"}
                        </button>
                    </div>
                    {showForm && (
                        <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="new-question-form">
                            <div className="sm:col-span-2">
                                <label className="label-caps block mb-2">Texto da pergunta *</label>
                                <textarea
                                    rows={2}
                                    className="input-clean w-full"
                                    value={draft.text}
                                    onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                                    placeholder="Ex: Sente-se apoiado pela sua equipa?"
                                    data-testid="new-question-text"
                                    required
                                />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Tipo de resposta</label>
                                <select
                                    className="input-clean w-full"
                                    value={draft.type}
                                    onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                                    data-testid="new-question-type"
                                >
                                    {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Ordem</label>
                                <input
                                    type="number"
                                    className="input-clean w-full"
                                    value={draft.order}
                                    onChange={(e) => setDraft({ ...draft, order: e.target.value })}
                                    data-testid="new-question-order"
                                />
                            </div>
                            {["single_choice", "multi_choice"].includes(draft.type) && (
                                <div className="sm:col-span-2">
                                    <label className="label-caps block mb-2">Opções (uma por linha) *</label>
                                    <textarea
                                        rows={4}
                                        className="input-clean w-full"
                                        value={draft.options}
                                        onChange={(e) => setDraft({ ...draft, options: e.target.value })}
                                        placeholder={"Nunca\nRaramente\nÀs vezes\nSempre"}
                                        data-testid="new-question-options"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="label-caps block mb-2">Âmbito</label>
                                <select
                                    className="input-clean w-full"
                                    value={draft.scope}
                                    onChange={(e) => setDraft({ ...draft, scope: e.target.value })}
                                    data-testid="new-question-scope"
                                >
                                    <option value="individual">Individual (cada colaborador responde)</option>
                                    <option value="collective">Coletiva (respondida pelos gestores)</option>
                                </select>
                            </div>
                            <div className="flex items-end">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={draft.mandatory}
                                        onChange={(e) => setDraft({ ...draft, mandatory: e.target.checked })}
                                        data-testid="new-question-mandatory"
                                    />
                                    <span className="text-[#0d1628]">Obrigatória</span>
                                </label>
                            </div>
                            {indices.length > 0 && (
                                <div className="sm:col-span-2">
                                    <label className="label-caps block mb-2">Índices ligados (opcional)</label>
                                    <div className="flex flex-wrap gap-2" data-testid="new-question-indices">
                                        {indices.map((ix) => (
                                            <button
                                                key={ix.id}
                                                type="button"
                                                onClick={() => toggleIndex(ix.id)}
                                                className={`text-xs px-3 py-1.5 border ${
                                                    draft.indices.includes(ix.id)
                                                        ? "bg-[#b89b6f] text-white border-[#b89b6f]"
                                                        : "bg-white text-[#52525b] border-[#e4e4e7]"
                                                }`}
                                                data-testid={`new-question-index-${ix.id}`}
                                            >
                                                {ix.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="sm:col-span-2 flex justify-end">
                                <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="new-question-save">
                                    <Save size={14} /> {saving ? "A guardar..." : "Guardar pergunta"}
                                </button>
                            </div>
                        </form>
                    )}
                </section>
            )}

            {loading ? (
                <div className="card-flat text-sm text-[#52525b]">A carregar perguntas...</div>
            ) : (
                <div className="space-y-8" data-testid="questions-groups">
                    {["fib", "whr", "global", "custom"].map((g) => {
                        const items = grouped[g];
                        if (!items || items.length === 0) return null;
                        return (
                            <section key={g} data-testid={`group-${g}`}>
                                <div className="label-caps mb-3">{GROUP_LABELS[g]} · {items.length}</div>
                                <ul className="space-y-2">
                                    {items.map((q) => {
                                        const isGlobal = q.mandatory_global;
                                        const canEdit = canManage && !isGlobal;
                                        return (
                                            <li
                                                key={q.id}
                                                className={`border p-4 flex flex-wrap gap-3 items-start justify-between ${q.active ? "border-[#e4e4e7] bg-white" : "border-[#e4e4e7] bg-[#fafaf9] opacity-70"}`}
                                                data-testid={`question-${q.id}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-[#b89b6f] font-display text-lg leading-none mt-1">{q.order}</span>
                                                        <span className="text-sm text-[#0d1628]">{q.text}</span>
                                                        {q.mandatory && <span className="text-[#dc2626] text-xs">*</span>}
                                                    </div>
                                                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#52525b]">
                                                        <span>{TYPE_LABELS[q.type] || q.type}</span>
                                                        <span>âmbito: {q.scope}</span>
                                                        {isGlobal && <span className="inline-flex items-center gap-1 text-[#b89b6f]"><Lock size={10} /> Global (IHTP)</span>}
                                                        {!q.active && <span className="text-[#dc2626]">Desativada</span>}
                                                    </div>
                                                    {q.options?.length > 0 && (
                                                        <div className="text-xs text-[#52525b] mt-2">
                                                            {q.options.map((o) => <span key={o} className="inline-block bg-[#f5f3ee] px-2 py-0.5 mr-1 mb-1">{o}</span>)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-1">
                                                    {canEdit && (
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleActive(q)}
                                                            className="btn-ghost text-xs"
                                                            data-testid={`question-toggle-${q.id}`}
                                                        >
                                                            {q.active ? "Desativar" : "Ativar"}
                                                        </button>
                                                    )}
                                                    {canEdit && (
                                                        <button
                                                            type="button"
                                                            onClick={() => remove(q)}
                                                            className="btn-ghost text-xs text-[#dc2626]"
                                                            data-testid={`question-delete-${q.id}`}
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
